import { useCallback, useMemo, useState, type KeyboardEvent } from 'react';

import { viewApi } from '@/api/services/viewApi';
import {
  Button,
  CheckBox,
  Icon,
  IconButton,
  NumericTextBox,
  Spinner,
  TextBox,
  useDialogService,
} from '@/components/ui';
import { cx } from '@/components/ui/classNames';
import { getCaption } from '@/types/captions';
import type { ChinaAppSysViewField } from '@/types/entities';
import type { EntityTypes } from '@/types/enums';

import { AdminNotice } from '../AdminNotice';
import { entityFields } from '../entityFields';
import { PickerDialog, type PickerOption } from '../PickerDialog';
import { useAdminList } from './adminList';

/**
 * Die Feldliste einer View — Port von `IwmAdminListViewFields.razor` (REQ-29 §2.2).
 *
 * Bewusst **kein** `DataGrid`: das Grid ist auf Serverdaten einer View mit Seitenaufteilung
 * zugeschnitten. Hier stehen die bereits geladenen Felder einer einzigen View.
 *
 * Die Reihenfolge wird gezogen und schreibt `Sortierung`; wer nicht zieht, verschiebt mit
 * Pfeil auf und ab auf dem Griff.
 */
export interface AdminViewFieldsListProps {
  parentId: number;
  entity: EntityTypes;
}

/** Spaltenüberschriften wie heute. */
const COLUMNS = ['Order', 'Property', 'Caption', 'Visible', 'Readonly', 'Width (px)'] as const;

const REORDER_HINT =
  'Die Reihenfolge bestimmt die Spaltenfolge im Grid. Ziehen Sie eine Zeile am Griff oder verschieben Sie sie mit Pfeil auf und ab.';
const SEARCH_HINT = 'Solange die Suche etwas filtert, bleibt die Reihenfolge gesperrt.';

/** Reihenfolge im Grid: `Sortierung` aufsteigend, bei Gleichstand der Feldname. */
function sortFields(fields: readonly ChinaAppSysViewField[]): ChinaAppSysViewField[] {
  return [...fields].sort(
    (left, right) =>
      left.Sortierung - right.Sortierung ||
      (left.FieldName ?? '').localeCompare(right.FieldName ?? '', 'de-DE'),
  );
}

/**
 * Verschiebt einen Eintrag und vergibt `Sortierung` lückenlos ab 1 neu. Unveränderte Zeilen
 * bleiben dieselben Objekte — daran erkennt das Speichern, welche Zeilen hinausgehen müssen.
 * Ohne Wirkung ist das Ergebnis `null`.
 */
function moveAndRenumber(
  fields: readonly ChinaAppSysViewField[],
  from: number,
  to: number,
): ChinaAppSysViewField[] | null {
  if (from === to || from < 0 || to < 0 || from >= fields.length || to >= fields.length) {
    return null;
  }

  const next = [...fields];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return null;
  next.splice(to, 0, moved);

  return next.map((field, index) =>
    field.Sortierung === index + 1 ? field : { ...field, Sortierung: index + 1 },
  );
}

/**
 * Pendant zu `new ChinaAppSysViewField() { … }` aus dem Auswahldialog des Altclients.
 * `BezeichnungDe` und `BezeichnungChn` gehen mit: `AddFieldToView` belegt zweimal
 * `BezeichnungChn` vor (`ViewsController.cs`), `BezeichnungDe` bliebe also ohne Wert.
 * `Sortierung` hängt das neue Feld hinten an; der Altclient legte es mit 0 an.
 */
function createViewField(
  entity: EntityTypes,
  fieldName: string,
  sortierung: number,
): ChinaAppSysViewField {
  return {
    Id: 0,
    // `default(DateTime)` so, wie System.Text.Json den Wert serialisiert.
    Created: '0001-01-01T00:00:00',
    CreatedByUserId: 0,
    Modified: '0001-01-01T00:00:00',
    ModifiedByUserId: 0,
    Timestamp: null,
    FieldName: fieldName,
    FieldCaption: getCaption(entity, fieldName),
    BezeichnungDe: '',
    BezeichnungChn: '',
    Visible: true,
    ReadOnly: false,
    Fixiert: false,
    Sortierung: sortierung,
    SpaltenBreite: 0,
    View: null,
  };
}

/** Rumpf des PUT ohne das Navigationsobjekt `View`. */
function fieldBody(field: ChinaAppSysViewField): ChinaAppSysViewField {
  return { ...field, View: null };
}

export function AdminViewFieldsList(props: AdminViewFieldsListProps) {
  const { entity, parentId } = props;
  const dialogService = useDialogService();
  const load = useCallback(() => viewApi.getFields(parentId), [parentId]);
  const { rows, setRows, loading, error, reload } = useAdminList(load);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<PickerOption<string>[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const sorted = useMemo(() => sortFields(rows), [rows]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return sorted;
    return sorted.filter((field) =>
      `${field.FieldName ?? ''} ${field.FieldCaption ?? ''}`.toLowerCase().includes(needle),
    );
  }, [search, sorted]);

  // Verschoben wird in der vollständigen Liste; filtert die Suche, wären die Nachbarn nicht
  // die sichtbaren. Dann bleiben die Griffe gesperrt.
  const reorderable = visible.length === sorted.length;

  /** Ersetzt eine Zeile, ohne neu zu laden — für die optimistische Anzeige. */
  const replace = (field: ChinaAppSysViewField) => {
    setRows((current) => current.map((entry) => (entry.Id === field.Id ? field : entry)));
  };

  /**
   * Speichert eine einzelne Zeile. Die Antwort trägt den neuen `Timestamp` und ersetzt die
   * Zeile deshalb noch einmal; sonst scheiterte die nächste Änderung an der optimistischen
   * Sperre. Ohne Antwort geht der vorige Stand zurück.
   */
  const save = async (previous: ChinaAppSysViewField, next: ChinaAppSysViewField) => {
    replace(next);
    const result = await viewApi.putField(parentId, fieldBody(next));
    replace(result ?? previous);
  };

  /** Nur die Zeilen, deren `Sortierung` sich geändert hat, gehen hinaus. */
  const move = async (from: number, to: number) => {
    const next = moveAndRenumber(sorted, from, to);
    if (next === null) return;

    const changed = next.filter((field, index) => field !== sorted[index]);
    setRows(next);
    setBusy(true);
    for (const field of changed) {
      const result = await viewApi.putField(parentId, fieldBody(field));
      if (result === null) {
        // Teilweise gespeichert: Der Stand des Servers ist jetzt die einzige Wahrheit.
        await reload();
        setBusy(false);
        return;
      }
      replace(result);
    }
    setBusy(false);
  };

  const onHandleKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    void move(index, event.key === 'ArrowUp' ? index - 1 : index + 1);
  };

  const openPicker = () => {
    const taken = new Set(sorted.map((field) => (field.FieldName ?? '').toLowerCase()));
    setOptions(
      entityFields(entity)
        .filter((field) => !taken.has(field.name.toLowerCase()))
        .map((field) => ({
          id: field.name,
          label: field.name,
          description: field.caption === field.name ? undefined : field.caption,
        })),
    );
  };

  const addFields = async (names: readonly string[]) => {
    setBusy(true);
    let sortierung = sorted.reduce((max, field) => Math.max(max, field.Sortierung), 0);
    for (const name of names) {
      sortierung += 1;
      await viewApi.addField(parentId, createViewField(entity, name, sortierung));
    }
    await reload();
    setBusy(false);
  };

  const remove = async (field: ChinaAppSysViewField) => {
    const confirmed = await dialogService.confirm(
      `Soll das Feld „${field.FieldName ?? ''}" aus dieser View entfernt werden?`,
      'Feld entfernen',
    );
    if (!confirmed) return;
    setBusy(true);
    await viewApi.deleteField(parentId, field.Id);
    await reload();
    setBusy(false);
  };

  const body = () => {
    if (loading) {
      return (
        <div className="iwm-state-loading">
          <span className="iwm-state-spinner">
            <Spinner size={20} />
          </span>
          Wird geladen …
        </div>
      );
    }
    if (error !== null) {
      return <AdminNotice tone="danger">Die Felder konnten nicht geladen werden.</AdminNotice>;
    }
    if (sorted.length === 0) {
      return (
        <AdminNotice tone="warning">
          Diese View hat keine Felder. Im Grid bliebe sie leer.
        </AdminNotice>
      );
    }

    return (
      <div className="iwm-admin-sublist-wrap">
        <table className="iwm-admin-sublist-table">
          <thead>
            <tr>
              <th scope="col" className="iwm-admin-sublist-handle">
                <span className="iwm-admin-sr-only">Reihenfolge</span>
              </th>
              {COLUMNS.map((column) => (
                <th key={column} scope="col">
                  {column}
                </th>
              ))}
              <th scope="col" className="iwm-admin-sublist-actions">
                <span className="iwm-admin-sr-only">Remove</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((field) => {
              const name = field.FieldName ?? '';
              const index = sorted.findIndex((entry) => entry.Id === field.Id);
              return (
                <tr
                  key={field.Id}
                  className={cx(
                    dragIndex === index && 'iwm-admin-row-dragging',
                    overIndex === index && dragIndex !== null && 'iwm-admin-row-drop',
                  )}
                  onDragEnter={() => {
                    setOverIndex(index);
                  }}
                  onDragOver={(event) => {
                    event.preventDefault();
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (dragIndex !== null) void move(dragIndex, index);
                    setDragIndex(null);
                    setOverIndex(null);
                  }}
                >
                  <td className="iwm-admin-sublist-handle">
                    <button
                      type="button"
                      draggable={reorderable}
                      disabled={!reorderable || busy}
                      className="iwm-admin-drag-handle"
                      title={reorderable ? undefined : SEARCH_HINT}
                      aria-label={`${name} verschieben, Position ${String(index + 1)} von ${String(sorted.length)}`}
                      onDragStart={() => {
                        setDragIndex(index);
                      }}
                      onDragEnd={() => {
                        setDragIndex(null);
                        setOverIndex(null);
                      }}
                      onKeyDown={(event) => {
                        onHandleKey(event, index);
                      }}
                    >
                      <Icon iconClass="fa-solid fa-grip-vertical" />
                    </button>
                  </td>
                  <td className="iwm-admin-sublist-number">{field.Sortierung}</td>
                  <td>{name}</td>
                  <td>
                    <TextBox
                      className="iwm-admin-cell-field"
                      label={`Caption: ${name}`}
                      value={field.FieldCaption}
                      onChange={(value) => {
                        void save(field, { ...field, FieldCaption: value });
                      }}
                    />
                  </td>
                  <td className="iwm-admin-sublist-center">
                    <CheckBox
                      className="iwm-admin-sublist-check"
                      label={`Visible: ${name}`}
                      checked={field.Visible}
                      onChange={(checked) => {
                        void save(field, { ...field, Visible: checked });
                      }}
                    />
                  </td>
                  <td className="iwm-admin-sublist-center">
                    <CheckBox
                      className="iwm-admin-sublist-check"
                      label={`Readonly: ${name}`}
                      checked={field.ReadOnly}
                      onChange={(checked) => {
                        void save(field, { ...field, ReadOnly: checked });
                      }}
                    />
                  </td>
                  <td className="iwm-admin-sublist-number">
                    <NumericTextBox
                      className="iwm-admin-cell-field"
                      label={`Width (px): ${name}`}
                      format="d"
                      min={0}
                      value={field.SpaltenBreite}
                      onChange={(value) => {
                        void save(field, { ...field, SpaltenBreite: value ?? 0 });
                      }}
                    />
                  </td>
                  <td className="iwm-admin-sublist-actions">
                    <IconButton
                      iconClass="fa-solid fa-xmark"
                      title="Remove"
                      disabled={busy}
                      onClick={() => {
                        void remove(field);
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <section className="iwm-admin-sublist">
      <div className="iwm-admin-sublist-head">
        <h4 className="iwm-admin-sublist-title">Felder</h4>
        <Button content="Add" iconClass="fa-solid fa-plus" disabled={busy} onClick={openPicker} />
      </div>

      <span className="e-input-group iwm-admin-search">
        <input
          type="search"
          className="e-input"
          placeholder="Search"
          aria-label="Search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        <span className="e-input-group-icon e-search-icon e-icons fa-solid fa-magnifying-glass" />
      </span>

      {body()}

      <p className="iwm-admin-sublist-hint">
        {REORDER_HINT}
        {reorderable ? null : ` ${SEARCH_HINT}`}
      </p>

      <PickerDialog<string>
        open={options !== null}
        title="Felder auswählen"
        options={options ?? []}
        emptyText="Alle Felder dieser Entität stehen bereits in der View."
        onClose={() => {
          setOptions(null);
        }}
        onSubmit={async (names) => {
          await addFields(names);
        }}
      />
    </section>
  );
}

export default AdminViewFieldsList;
